import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const InvitationIdSchema = Type.String({ pattern: "^invite_[a-f0-9]{32}$" });
const InvitationAccountIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const InvitationDateTimeSchema = Type.String({ format: "date-time" });
const InvitationTokenSchema = Type.String({ minLength: 32, maxLength: 512 });
const InvitationIdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const InvitationEmployeeRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
]);
const InvitationEmployeeRolesSchema = Type.Array(InvitationEmployeeRoleSchema, {
  minItems: 1,
  maxItems: 3,
  uniqueItems: true,
});
const WorkforceAccountRecoveryRoleSchema = Type.Union([
  InvitationEmployeeRoleSchema,
  Type.Literal("COACH"),
]);
const WorkforceAccountRecoveryRolesSchema = Type.Array(WorkforceAccountRecoveryRoleSchema, {
  minItems: 1,
  maxItems: 4,
  uniqueItems: true,
});

export const WorkforceInvitationPurposeSchema = Type.Union([
  Type.Literal("ACCOUNT_ACTIVATION"),
  Type.Literal("PASSWORD_RESET"),
  Type.Literal("WECHAT_REBIND"),
  Type.Literal("PHONE_CHANGE"),
]);
export type WorkforceInvitationPurpose = Static<typeof WorkforceInvitationPurposeSchema>;

export const WorkforceInvitationStatusSchema = Type.Union([
  Type.Literal("PENDING_IDENTITY_CONFIRMATION"),
  Type.Literal("OTP_PENDING"),
  Type.Literal("OTP_VERIFIED"),
  Type.Literal("NEW_PHONE_OTP_PENDING"),
  Type.Literal("PHONE_CHANGE_READY"),
  Type.Literal("WECHAT_BOUND"),
  Type.Literal("ACTIVE"),
  Type.Literal("DELIVERY_FAILED"),
  Type.Literal("EXPIRED"),
  Type.Literal("CANCELLED"),
  Type.Literal("LOCKED"),
]);
export type WorkforceInvitationStatus = Static<typeof WorkforceInvitationStatusSchema>;

export const WorkforceInvitationProviderModeSchema = Type.Union([
  Type.Literal("SIMULATED"),
  Type.Literal("EXTERNAL"),
]);
export type WorkforceInvitationProviderMode = Static<typeof WorkforceInvitationProviderModeSchema>;

export const WorkforceInvitationEvidenceSchema = Type.Union([
  Type.Literal("PROVIDER_SIMULATED"),
  Type.Literal("PROVIDER_ACCEPTED"),
]);
export type WorkforceInvitationEvidence = Static<typeof WorkforceInvitationEvidenceSchema>;

/**
 * Issues one invitation for an already-existing person and current employment.
 * Roles, person details and store scope are authoritative server facts and may
 * not be repeated or changed by this command.
 */
const CreateWorkforceInvitationCommonFields = {
  reason: Type.String({ minLength: 2, maxLength: 500 }),
  idempotencyKey: InvitationIdempotencyKeySchema,
};

const CreateWorkforceInvitationNewPersonCommandSchema = Type.Object(
  {
    ...CreateWorkforceInvitationCommonFields,
    personMode: Type.Literal("NEW_PERSON"),
    person: Type.Object(
      {
        personId: InvitationAccountIdSchema,
        displayName: Type.String({ minLength: 1, maxLength: 80 }),
      },
      { additionalProperties: false },
    ),
    employment: Type.Object(
      {
        storeId: InvitationAccountIdSchema,
        employmentNumber: Type.String({ minLength: 1, maxLength: 80 }),
        positionTitle: Type.String({ minLength: 1, maxLength: 80 }),
        roles: InvitationEmployeeRolesSchema,
      },
      { additionalProperties: false },
    ),
    expectedAggregateVersion: Type.Null(),
  },
  { additionalProperties: false },
);

const CreateWorkforceInvitationExistingPersonCommandSchema = Type.Object(
  {
    ...CreateWorkforceInvitationCommonFields,
    personMode: Type.Literal("EXISTING_PERSON"),
    person: Type.Object({ personId: InvitationAccountIdSchema }, { additionalProperties: false }),
    employment: Type.Object(
      {
        storeId: InvitationAccountIdSchema,
        employmentId: InvitationAccountIdSchema,
        expectedEmploymentVersion: Type.Integer({ minimum: 1 }),
      },
      { additionalProperties: false },
    ),
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const CreateWorkforceInvitationCommandSchema = Type.Union([
  CreateWorkforceInvitationNewPersonCommandSchema,
  CreateWorkforceInvitationExistingPersonCommandSchema,
]);
export type CreateWorkforceInvitationCommand = Static<
  typeof CreateWorkforceInvitationCommandSchema
>;

/**
 * Internal Gateway-to-Cloud issue command. The Gateway owns token generation
 * and durable retry so Cloud only needs to persist a digest. This command is
 * never accepted directly from a browser.
 */
const IssueWorkforceInvitationNewPersonCommandSchema = Type.Object(
  {
    ...CreateWorkforceInvitationNewPersonCommandSchema.properties,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);

const IssueWorkforceInvitationExistingPersonCommandSchema = Type.Object(
  {
    ...CreateWorkforceInvitationExistingPersonCommandSchema.properties,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);

export const IssueWorkforceInvitationCommandSchema = Type.Union([
  IssueWorkforceInvitationNewPersonCommandSchema,
  IssueWorkforceInvitationExistingPersonCommandSchema,
]);
export type IssueWorkforceInvitationCommand = Static<typeof IssueWorkforceInvitationCommandSchema>;

/**
 * Starts a manager-approved recovery for one active account. The target employee
 * still has to complete the public identity, OTP and WeChat checks before a new
 * password is accepted. Neither the browser nor the approving manager provides
 * the new password here.
 */
export const CreateWorkforcePasswordResetCommandSchema = Type.Object(
  {
    personId: InvitationAccountIdSchema,
    employmentId: InvitationAccountIdSchema,
    accountId: InvitationAccountIdSchema,
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
    expectedEmploymentVersion: Type.Integer({ minimum: 1 }),
    expectedIdentityVersion: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateWorkforcePasswordResetCommand = Static<
  typeof CreateWorkforcePasswordResetCommandSchema
>;

export const IssueWorkforcePasswordResetCommandSchema = Type.Object(
  {
    ...CreateWorkforcePasswordResetCommandSchema.properties,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);
export type IssueWorkforcePasswordResetCommand = Static<
  typeof IssueWorkforcePasswordResetCommandSchema
>;

/**
 * Starts a self-service WeChat rebind for the currently authenticated account.
 * The browser deliberately supplies no person, employment or account id: the
 * Gateway must derive the target from the verified personal session.
 */
export const CreateWorkforceWechatRebindCommandSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateWorkforceWechatRebindCommand = Static<
  typeof CreateWorkforceWechatRebindCommandSchema
>;

/**
 * Internal Gateway-to-Cloud command. All identity and version fields are
 * derived from one trusted WorkforcePrincipalContext and are never accepted
 * from the browser.
 */
export const IssueWorkforceWechatRebindCommandSchema = Type.Object(
  {
    personId: InvitationAccountIdSchema,
    employmentId: InvitationAccountIdSchema,
    accountId: InvitationAccountIdSchema,
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
    expectedEmploymentVersion: Type.Integer({ minimum: 1 }),
    expectedIdentityVersion: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);
export type IssueWorkforceWechatRebindCommand = Static<
  typeof IssueWorkforceWechatRebindCommandSchema
>;

/**
 * Starts a self-service phone change for the currently authenticated account.
 * The browser deliberately supplies no target identity or version. The new
 * phone is collected only after the existing verified phone has passed OTP.
 */
export const CreateWorkforcePhoneChangeCommandSchema = Type.Object(
  {
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateWorkforcePhoneChangeCommand = Static<
  typeof CreateWorkforcePhoneChangeCommandSchema
>;

/** Trusted Gateway-to-Cloud command derived from one personal session. */
export const IssueWorkforcePhoneChangeCommandSchema = Type.Object(
  {
    personId: InvitationAccountIdSchema,
    employmentId: InvitationAccountIdSchema,
    accountId: InvitationAccountIdSchema,
    expectedAggregateVersion: Type.Integer({ minimum: 1 }),
    expectedEmploymentVersion: Type.Integer({ minimum: 1 }),
    expectedIdentityVersion: Type.Integer({ minimum: 1 }),
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);
export type IssueWorkforcePhoneChangeCommand = Static<
  typeof IssueWorkforcePhoneChangeCommandSchema
>;

const WorkforceInvitationIssueResultFields = {
  purpose: Type.Optional(WorkforceInvitationPurposeSchema),
  invitationId: InvitationIdSchema,
  personId: InvitationAccountIdSchema,
  employmentId: InvitationAccountIdSchema,
  accountId: InvitationAccountIdSchema,
  storeId: InvitationAccountIdSchema,
  displayName: Type.String({ minLength: 1, maxLength: 80 }),
  roles: WorkforceAccountRecoveryRolesSchema,
  status: Type.Literal("PENDING_IDENTITY_CONFIRMATION"),
  issuedAt: InvitationDateTimeSchema,
  expiresAt: InvitationDateTimeSchema,
  employmentVersion: Type.Integer({ minimum: 1 }),
  identityVersion: Type.Integer({ minimum: 1 }),
  aggregateVersion: Type.Integer({ minimum: 1 }),
  projectionRevision: Type.Integer({ minimum: 1 }),
  idempotencyKey: InvitationIdempotencyKeySchema,
};

export const WorkforceInvitationIssueResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      providerMode: Type.Literal("SIMULATED"),
      activationToken: InvitationTokenSchema,
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      providerMode: Type.Literal("EXTERNAL"),
      activationToken: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceInvitationIssueResult = Static<typeof WorkforceInvitationIssueResultSchema>;

export const WorkforceWechatRebindIssueResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      purpose: Type.Literal("WECHAT_REBIND"),
      providerMode: Type.Literal("SIMULATED"),
      activationToken: InvitationTokenSchema,
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      purpose: Type.Literal("WECHAT_REBIND"),
      providerMode: Type.Literal("EXTERNAL"),
      activationToken: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforceWechatRebindIssueResult = Static<
  typeof WorkforceWechatRebindIssueResultSchema
>;

export const WorkforcePhoneChangeIssueResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      purpose: Type.Literal("PHONE_CHANGE"),
      providerMode: Type.Literal("SIMULATED"),
      activationToken: InvitationTokenSchema,
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceInvitationIssueResultFields,
      purpose: Type.Literal("PHONE_CHANGE"),
      providerMode: Type.Literal("EXTERNAL"),
      activationToken: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforcePhoneChangeIssueResult = Static<typeof WorkforcePhoneChangeIssueResultSchema>;

export const InspectWorkforceInvitationCommandSchema = Type.Object(
  { activationToken: InvitationTokenSchema },
  { additionalProperties: false },
);
export type InspectWorkforceInvitationCommand = Static<
  typeof InspectWorkforceInvitationCommandSchema
>;

export const InspectWorkforceInvitationResultSchema = Type.Object(
  {
    purpose: Type.Optional(WorkforceInvitationPurposeSchema),
    invitationId: InvitationIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    roles: WorkforceAccountRecoveryRolesSchema,
    status: WorkforceInvitationStatusSchema,
    localCompletionRequired: Type.Optional(Type.Boolean()),
    providerMode: WorkforceInvitationProviderModeSchema,
    issuedAt: InvitationDateTimeSchema,
    expiresAt: InvitationDateTimeSchema,
    evidence: WorkforceInvitationEvidenceSchema,
  },
  { additionalProperties: false },
);
export type InspectWorkforceInvitationResult = Static<
  typeof InspectWorkforceInvitationResultSchema
>;

const WorkforceInvitationOpenStatusSchema = Type.Union([
  Type.Literal("PENDING_IDENTITY_CONFIRMATION"),
  Type.Literal("OTP_PENDING"),
  Type.Literal("OTP_VERIFIED"),
  Type.Literal("WECHAT_BOUND"),
]);
const WorkforceInvitationReissuableStatusSchema = Type.Union([
  Type.Literal("DELIVERY_FAILED"),
  Type.Literal("EXPIRED"),
  Type.Literal("CANCELLED"),
  Type.Literal("LOCKED"),
]);

const WorkforceInvitationAdminReadModelFields = {
  purpose: Type.Optional(WorkforceInvitationPurposeSchema),
  invitationId: InvitationIdSchema,
  personId: InvitationAccountIdSchema,
  employmentId: InvitationAccountIdSchema,
  accountId: InvitationAccountIdSchema,
  storeId: InvitationAccountIdSchema,
  displayName: Type.String({ minLength: 1, maxLength: 80 }),
  roles: WorkforceAccountRecoveryRolesSchema,
  status: WorkforceInvitationStatusSchema,
  providerMode: WorkforceInvitationProviderModeSchema,
  issuedAt: InvitationDateTimeSchema,
  expiresAt: InvitationDateTimeSchema,
  completedAt: Type.Union([InvitationDateTimeSchema, Type.Null()]),
  evidence: WorkforceInvitationEvidenceSchema,
};

export const WorkforceInvitationAdminReadModelSchema = Type.Object(
  WorkforceInvitationAdminReadModelFields,
  { additionalProperties: false },
);
export type WorkforceInvitationAdminReadModel = Static<
  typeof WorkforceInvitationAdminReadModelSchema
>;

export const CancelWorkforceInvitationCommandSchema = Type.Object(
  {
    invitationId: InvitationIdSchema,
    personId: InvitationAccountIdSchema,
    expectedStatus: WorkforceInvitationOpenStatusSchema,
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CancelWorkforceInvitationCommand = Static<
  typeof CancelWorkforceInvitationCommandSchema
>;

export const CancelWorkforceInvitationResultSchema = Type.Object(
  {
    ...WorkforceInvitationAdminReadModelFields,
    status: Type.Literal("CANCELLED"),
    completedAt: InvitationDateTimeSchema,
    cancelledAt: InvitationDateTimeSchema,
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CancelWorkforceInvitationResult = Static<typeof CancelWorkforceInvitationResultSchema>;

export const ReissueWorkforceInvitationCommandSchema = Type.Object(
  {
    invitationId: InvitationIdSchema,
    personId: InvitationAccountIdSchema,
    expectedStatus: WorkforceInvitationReissuableStatusSchema,
    reason: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReissueWorkforceInvitationCommand = Static<
  typeof ReissueWorkforceInvitationCommandSchema
>;

export const ReissueWorkforceInvitationInternalCommandSchema = Type.Object(
  {
    ...ReissueWorkforceInvitationCommandSchema.properties,
    activationToken: InvitationTokenSchema,
  },
  { additionalProperties: false },
);
export type ReissueWorkforceInvitationInternalCommand = Static<
  typeof ReissueWorkforceInvitationInternalCommandSchema
>;

export const ConfirmWorkforceInvitationIdentityCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    phone: Type.String({
      minLength: 6,
      maxLength: 40,
      pattern: "^[0-9+\\s()（）._:/／-]+$",
    }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ConfirmWorkforceInvitationIdentityCommand = Static<
  typeof ConfirmWorkforceInvitationIdentityCommandSchema
>;

const WorkforceInvitationOtpChallengeFields = {
  invitationId: InvitationIdSchema,
  status: Type.Literal("OTP_PENDING"),
  challengeId: Type.String({ pattern: "^otp_[a-f0-9]{32}$" }),
  maskedPhone: Type.String({ minLength: 5, maxLength: 40 }),
  otpExpiresAt: InvitationDateTimeSchema,
  retryAfterSeconds: Type.Integer({ minimum: 0 }),
  idempotencyKey: InvitationIdempotencyKeySchema,
};

export const ConfirmWorkforceInvitationIdentityResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceInvitationOtpChallengeFields,
      providerMode: Type.Literal("SIMULATED"),
      simulatedOtpCode: Type.String({ pattern: "^[0-9]{6}$" }),
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceInvitationOtpChallengeFields,
      providerMode: Type.Literal("EXTERNAL"),
      simulatedOtpCode: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type ConfirmWorkforceInvitationIdentityResult = Static<
  typeof ConfirmWorkforceInvitationIdentityResultSchema
>;

export const VerifyWorkforceInvitationOtpCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    challengeId: Type.String({ pattern: "^otp_[a-f0-9]{32}$" }),
    otpCode: Type.String({ pattern: "^[0-9]{6}$" }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type VerifyWorkforceInvitationOtpCommand = Static<
  typeof VerifyWorkforceInvitationOtpCommandSchema
>;

const WorkforceInvitationWechatChallengeFields = {
  invitationId: InvitationIdSchema,
  status: Type.Literal("OTP_VERIFIED"),
  challengeId: Type.String({ pattern: "^wechat_[a-f0-9]{32}$" }),
  idempotencyKey: InvitationIdempotencyKeySchema,
};

export const VerifyWorkforceInvitationOtpResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforceInvitationWechatChallengeFields,
      providerMode: Type.Literal("SIMULATED"),
      simulatedAuthorizationCode: Type.String({ minLength: 16, maxLength: 256 }),
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforceInvitationWechatChallengeFields,
      providerMode: Type.Literal("EXTERNAL"),
      simulatedAuthorizationCode: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      invitationId: InvitationIdSchema,
      purpose: Type.Literal("PHONE_CHANGE"),
      status: Type.Literal("OTP_VERIFIED"),
      nextStep: Type.Literal("VERIFY_NEW_PHONE"),
      idempotencyKey: InvitationIdempotencyKeySchema,
      providerMode: Type.Literal("SIMULATED"),
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      invitationId: InvitationIdSchema,
      purpose: Type.Literal("PHONE_CHANGE"),
      status: Type.Literal("OTP_VERIFIED"),
      nextStep: Type.Literal("VERIFY_NEW_PHONE"),
      idempotencyKey: InvitationIdempotencyKeySchema,
      providerMode: Type.Literal("EXTERNAL"),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type VerifyWorkforceInvitationOtpResult = Static<
  typeof VerifyWorkforceInvitationOtpResultSchema
>;

/** Starts verification of the replacement phone after the current phone OTP passed. */
export const RequestWorkforcePhoneChangeNewPhoneOtpCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    newPhone: Type.String({
      minLength: 6,
      maxLength: 40,
      pattern: "^[0-9+\\s()（）._:/／-]+$",
    }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RequestWorkforcePhoneChangeNewPhoneOtpCommand = Static<
  typeof RequestWorkforcePhoneChangeNewPhoneOtpCommandSchema
>;

const WorkforcePhoneChangeNewPhoneOtpFields = {
  invitationId: InvitationIdSchema,
  purpose: Type.Literal("PHONE_CHANGE"),
  status: Type.Literal("NEW_PHONE_OTP_PENDING"),
  challengeId: Type.String({ pattern: "^new_phone_otp_[a-f0-9]{32}$" }),
  maskedNewPhone: Type.String({ minLength: 5, maxLength: 40 }),
  otpExpiresAt: InvitationDateTimeSchema,
  retryAfterSeconds: Type.Integer({ minimum: 0 }),
  idempotencyKey: InvitationIdempotencyKeySchema,
};

export const WorkforcePhoneChangeNewPhoneOtpResultSchema = Type.Union([
  Type.Object(
    {
      ...WorkforcePhoneChangeNewPhoneOtpFields,
      providerMode: Type.Literal("SIMULATED"),
      simulatedOtpCode: Type.String({ pattern: "^[0-9]{6}$" }),
      evidence: Type.Literal("PROVIDER_SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...WorkforcePhoneChangeNewPhoneOtpFields,
      providerMode: Type.Literal("EXTERNAL"),
      simulatedOtpCode: Type.Null(),
      evidence: Type.Literal("PROVIDER_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type WorkforcePhoneChangeNewPhoneOtpResult = Static<
  typeof WorkforcePhoneChangeNewPhoneOtpResultSchema
>;

export const VerifyWorkforcePhoneChangeNewPhoneOtpCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    challengeId: Type.String({ pattern: "^new_phone_otp_[a-f0-9]{32}$" }),
    otpCode: Type.String({ pattern: "^[0-9]{6}$" }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type VerifyWorkforcePhoneChangeNewPhoneOtpCommand = Static<
  typeof VerifyWorkforcePhoneChangeNewPhoneOtpCommandSchema
>;

export const VerifyWorkforcePhoneChangeNewPhoneOtpResultSchema = Type.Object(
  {
    invitationId: InvitationIdSchema,
    purpose: Type.Literal("PHONE_CHANGE"),
    status: Type.Literal("PHONE_CHANGE_READY"),
    maskedNewPhone: Type.String({ minLength: 5, maxLength: 40 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
    evidence: WorkforceInvitationEvidenceSchema,
  },
  { additionalProperties: false },
);
export type VerifyWorkforcePhoneChangeNewPhoneOtpResult = Static<
  typeof VerifyWorkforcePhoneChangeNewPhoneOtpResultSchema
>;

export const BindWorkforceInvitationWechatCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    challengeId: Type.String({ pattern: "^wechat_[a-f0-9]{32}$" }),
    authorizationCode: Type.String({ minLength: 8, maxLength: 512 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type BindWorkforceInvitationWechatCommand = Static<
  typeof BindWorkforceInvitationWechatCommandSchema
>;

export const BindWorkforceInvitationWechatResultSchema = Type.Object(
  {
    invitationId: InvitationIdSchema,
    status: Type.Literal("WECHAT_BOUND"),
    maskedWechat: Type.String({ minLength: 3, maxLength: 40 }),
    activationReady: Type.Literal(true),
    idempotencyKey: InvitationIdempotencyKeySchema,
    evidence: WorkforceInvitationEvidenceSchema,
  },
  { additionalProperties: false },
);
export type BindWorkforceInvitationWechatResult = Static<
  typeof BindWorkforceInvitationWechatResultSchema
>;

export const CompleteWorkforceWechatRebindCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CompleteWorkforceWechatRebindCommand = Static<
  typeof CompleteWorkforceWechatRebindCommandSchema
>;

export const WorkforceWechatRebindResultSchema = Type.Object(
  {
    purpose: Type.Literal("WECHAT_REBIND"),
    invitationId: InvitationIdSchema,
    personId: InvitationAccountIdSchema,
    employmentId: InvitationAccountIdSchema,
    accountId: InvitationAccountIdSchema,
    storeId: InvitationAccountIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    roles: WorkforceAccountRecoveryRolesSchema,
    status: Type.Literal("ACTIVE"),
    maskedWechat: Type.String({ minLength: 3, maxLength: 40 }),
    identityVersion: Type.Integer({ minimum: 1 }),
    aggregateVersion: Type.Integer({ minimum: 1 }),
    projectionRevision: Type.Integer({ minimum: 1 }),
    completedAt: InvitationDateTimeSchema,
    idempotencyKey: InvitationIdempotencyKeySchema,
    evidence: WorkforceInvitationEvidenceSchema,
  },
  { additionalProperties: false },
);
export type WorkforceWechatRebindResult = Static<typeof WorkforceWechatRebindResultSchema>;

export const CompleteWorkforcePhoneChangeCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CompleteWorkforcePhoneChangeCommand = Static<
  typeof CompleteWorkforcePhoneChangeCommandSchema
>;

const WorkforcePhoneChangeResultFields = {
  purpose: Type.Literal("PHONE_CHANGE"),
  invitationId: InvitationIdSchema,
  personId: InvitationAccountIdSchema,
  employmentId: InvitationAccountIdSchema,
  accountId: InvitationAccountIdSchema,
  storeId: InvitationAccountIdSchema,
  displayName: Type.String({ minLength: 1, maxLength: 80 }),
  roles: WorkforceAccountRecoveryRolesSchema,
  status: Type.Literal("ACTIVE"),
  maskedOldPhone: Type.String({ minLength: 5, maxLength: 40 }),
  maskedNewPhone: Type.String({ minLength: 5, maxLength: 40 }),
  identityVersion: Type.Integer({ minimum: 1 }),
  aggregateVersion: Type.Integer({ minimum: 1 }),
  projectionRevision: Type.Integer({ minimum: 1 }),
  completedAt: InvitationDateTimeSchema,
  idempotencyKey: InvitationIdempotencyKeySchema,
  evidence: WorkforceInvitationEvidenceSchema,
};

/** Public Gateway-to-browser completion receipt. It never exposes either phone. */
export const WorkforcePhoneChangeResultSchema = Type.Object(WorkforcePhoneChangeResultFields, {
  additionalProperties: false,
});
export type WorkforcePhoneChangeResult = Static<typeof WorkforcePhoneChangeResultSchema>;

/** Internal Cloud-to-Gateway receipt used to replace the local password login atomically. */
export const WorkforcePhoneChangeCloudResultSchema = Type.Object(
  {
    ...WorkforcePhoneChangeResultFields,
    normalizedNewPhone: Type.String({ minLength: 6, maxLength: 20, pattern: "^[0-9]+$" }),
  },
  { additionalProperties: false },
);
export type WorkforcePhoneChangeCloudResult = Static<typeof WorkforcePhoneChangeCloudResultSchema>;

export const ActivateWorkforceInvitationCommandSchema = Type.Object(
  {
    activationToken: InvitationTokenSchema,
    backupPassword: Type.String({ minLength: 12, maxLength: 128 }),
    idempotencyKey: InvitationIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ActivateWorkforceInvitationCommand = Static<
  typeof ActivateWorkforceInvitationCommandSchema
>;

export const WorkforceInvitationActivationResultSchema = Type.Object(
  {
    purpose: Type.Optional(WorkforceInvitationPurposeSchema),
    invitationId: InvitationIdSchema,
    personId: InvitationAccountIdSchema,
    employmentId: InvitationAccountIdSchema,
    accountId: InvitationAccountIdSchema,
    storeId: InvitationAccountIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    roles: WorkforceAccountRecoveryRolesSchema,
    status: Type.Literal("ACTIVE"),
    login: Type.String({ pattern: "^[a-z0-9][a-z0-9._-]{2,63}$" }),
    identityVersion: Type.Integer({ minimum: 1 }),
    aggregateVersion: Type.Integer({ minimum: 1 }),
    projectionRevision: Type.Integer({ minimum: 1 }),
    revokedSessionCount: Type.Optional(Type.Integer({ minimum: 0 })),
    activatedAt: InvitationDateTimeSchema,
    idempotencyKey: InvitationIdempotencyKeySchema,
    evidence: WorkforceInvitationEvidenceSchema,
  },
  { additionalProperties: false },
);
export type WorkforceInvitationActivationResult = Static<
  typeof WorkforceInvitationActivationResultSchema
>;

export function isCreateWorkforceInvitationCommand(
  value: unknown,
): value is CreateWorkforceInvitationCommand {
  return Value.Check(CreateWorkforceInvitationCommandSchema, value);
}

export function isIssueWorkforceInvitationCommand(
  value: unknown,
): value is IssueWorkforceInvitationCommand {
  return Value.Check(IssueWorkforceInvitationCommandSchema, value);
}

export function isCreateWorkforcePasswordResetCommand(
  value: unknown,
): value is CreateWorkforcePasswordResetCommand {
  return Value.Check(CreateWorkforcePasswordResetCommandSchema, value);
}

export function isIssueWorkforcePasswordResetCommand(
  value: unknown,
): value is IssueWorkforcePasswordResetCommand {
  return Value.Check(IssueWorkforcePasswordResetCommandSchema, value);
}

export function isCreateWorkforceWechatRebindCommand(
  value: unknown,
): value is CreateWorkforceWechatRebindCommand {
  return Value.Check(CreateWorkforceWechatRebindCommandSchema, value);
}

export function isIssueWorkforceWechatRebindCommand(
  value: unknown,
): value is IssueWorkforceWechatRebindCommand {
  return Value.Check(IssueWorkforceWechatRebindCommandSchema, value);
}

export function isCreateWorkforcePhoneChangeCommand(
  value: unknown,
): value is CreateWorkforcePhoneChangeCommand {
  return Value.Check(CreateWorkforcePhoneChangeCommandSchema, value);
}

export function isIssueWorkforcePhoneChangeCommand(
  value: unknown,
): value is IssueWorkforcePhoneChangeCommand {
  return Value.Check(IssueWorkforcePhoneChangeCommandSchema, value);
}

export function isWorkforceInvitationIssueResult(
  value: unknown,
): value is WorkforceInvitationIssueResult {
  return Value.Check(WorkforceInvitationIssueResultSchema, value);
}

export function isWorkforceWechatRebindIssueResult(
  value: unknown,
): value is WorkforceWechatRebindIssueResult {
  return Value.Check(WorkforceWechatRebindIssueResultSchema, value);
}

export function isWorkforcePhoneChangeIssueResult(
  value: unknown,
): value is WorkforcePhoneChangeIssueResult {
  return Value.Check(WorkforcePhoneChangeIssueResultSchema, value);
}

export function isInspectWorkforceInvitationResult(
  value: unknown,
): value is InspectWorkforceInvitationResult {
  return Value.Check(InspectWorkforceInvitationResultSchema, value);
}

export function isConfirmWorkforceInvitationIdentityResult(
  value: unknown,
): value is ConfirmWorkforceInvitationIdentityResult {
  return Value.Check(ConfirmWorkforceInvitationIdentityResultSchema, value);
}

export function isVerifyWorkforceInvitationOtpResult(
  value: unknown,
): value is VerifyWorkforceInvitationOtpResult {
  return Value.Check(VerifyWorkforceInvitationOtpResultSchema, value);
}

export function isRequestWorkforcePhoneChangeNewPhoneOtpCommand(
  value: unknown,
): value is RequestWorkforcePhoneChangeNewPhoneOtpCommand {
  return Value.Check(RequestWorkforcePhoneChangeNewPhoneOtpCommandSchema, value);
}

export function isWorkforcePhoneChangeNewPhoneOtpResult(
  value: unknown,
): value is WorkforcePhoneChangeNewPhoneOtpResult {
  return Value.Check(WorkforcePhoneChangeNewPhoneOtpResultSchema, value);
}

export function isVerifyWorkforcePhoneChangeNewPhoneOtpCommand(
  value: unknown,
): value is VerifyWorkforcePhoneChangeNewPhoneOtpCommand {
  return Value.Check(VerifyWorkforcePhoneChangeNewPhoneOtpCommandSchema, value);
}

export function isVerifyWorkforcePhoneChangeNewPhoneOtpResult(
  value: unknown,
): value is VerifyWorkforcePhoneChangeNewPhoneOtpResult {
  return Value.Check(VerifyWorkforcePhoneChangeNewPhoneOtpResultSchema, value);
}

export function isBindWorkforceInvitationWechatResult(
  value: unknown,
): value is BindWorkforceInvitationWechatResult {
  return Value.Check(BindWorkforceInvitationWechatResultSchema, value);
}

export function isCompleteWorkforceWechatRebindCommand(
  value: unknown,
): value is CompleteWorkforceWechatRebindCommand {
  return Value.Check(CompleteWorkforceWechatRebindCommandSchema, value);
}

export function isWorkforceWechatRebindResult(
  value: unknown,
): value is WorkforceWechatRebindResult {
  return Value.Check(WorkforceWechatRebindResultSchema, value);
}

export function isCompleteWorkforcePhoneChangeCommand(
  value: unknown,
): value is CompleteWorkforcePhoneChangeCommand {
  return Value.Check(CompleteWorkforcePhoneChangeCommandSchema, value);
}

export function isWorkforcePhoneChangeResult(value: unknown): value is WorkforcePhoneChangeResult {
  return Value.Check(WorkforcePhoneChangeResultSchema, value);
}

export function isWorkforcePhoneChangeCloudResult(
  value: unknown,
): value is WorkforcePhoneChangeCloudResult {
  return Value.Check(WorkforcePhoneChangeCloudResultSchema, value);
}

export function isWorkforceInvitationActivationResult(
  value: unknown,
): value is WorkforceInvitationActivationResult {
  return Value.Check(WorkforceInvitationActivationResultSchema, value);
}

export function isWorkforceInvitationAdminReadModel(
  value: unknown,
): value is WorkforceInvitationAdminReadModel {
  return Value.Check(WorkforceInvitationAdminReadModelSchema, value);
}

export function isCancelWorkforceInvitationCommand(
  value: unknown,
): value is CancelWorkforceInvitationCommand {
  return Value.Check(CancelWorkforceInvitationCommandSchema, value);
}

export function isCancelWorkforceInvitationResult(
  value: unknown,
): value is CancelWorkforceInvitationResult {
  return Value.Check(CancelWorkforceInvitationResultSchema, value);
}

export function isReissueWorkforceInvitationCommand(
  value: unknown,
): value is ReissueWorkforceInvitationCommand {
  return Value.Check(ReissueWorkforceInvitationCommandSchema, value);
}

export function isReissueWorkforceInvitationInternalCommand(
  value: unknown,
): value is ReissueWorkforceInvitationInternalCommand {
  return Value.Check(ReissueWorkforceInvitationInternalCommandSchema, value);
}
